package ui.device.detail.preview.screen

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import api.AppType
import compose.assets.Svg
import compose.assets.svg.Content_save
import org.burnoutcrew.reorderable.*
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.device_detail__layer_bottom__date_from_to
import ssoft.frontend.generated.resources.device_detail__layer_top__no_apps
import tk.mallumo.compose.navigation.navTo_AppDetailUI
import tk.mallumo.compose.navigation.navTo_AppKindergartenPreviewUI
import tk.mallumo.compose.navigation.navTo_AppPreviewWheelUI
import tools.findRoot
import tools.rememberString
import ui.app.assign.detail.AppDetailArgs
import ui.device.detail.preview.DevicePreviewScope
import ui.device.detail.preview.DevicePreviewVM
import ui.widget.LazyImage
import ui.widget.LoadingOverlayUI


@Composable
fun DevicePreviewScope.DeviceScreen03_Apps(nnvl: DevicePreviewScope.Wrapper) {

    val reorderState = rememberReorderableLazyListState(
        onMove = { from, to -> nnvl.adapterApps.swapOrder(from.index, to.index) },
        canDragOver = { _, _ -> true })

    Box(Modifier.fillMaxSize()) {
        LazyColumn(
            state = reorderState.listState,
            verticalArrangement = Arrangement.spacedBy(8.dp),
            contentPadding = PaddingValues(16.dp),
            modifier = Modifier.fillMaxSize()
                .reorderable(reorderState)
        ) {
            itemsIndexed(
                items = nnvl.adapterApps.state,
                key = { _, it -> it.key },
            ) { index, item ->
                ReorderableItem(
                    reorderableState = reorderState,
                    key = item.key,
                    index = index
                ) { isDragging ->

                    val elevation = animateDpAsState(if (isDragging) 8.dp else 0.dp)

                    val color = animateColorAsState(
                        if (isDragging) MaterialTheme.colorScheme.primaryContainer
                        else MaterialTheme.colorScheme.surfaceContainer
                    )

                    ElevatedCard(
                        elevation = CardDefaults.cardElevation(
                            defaultElevation = elevation.value,
                            pressedElevation = elevation.value,
                            focusedElevation = elevation.value,
                            hoveredElevation = elevation.value,
                            draggedElevation = elevation.value,
                            disabledElevation = elevation.value
                        ),
                        colors = CardDefaults.cardColors(color.value)
                    ) {
                        AppEntry(
                            index = index,
                            nnvl = nnvl,
                            item = item,
                            reorderState = reorderState
                        )
                    }
                }
            }
        }
        if (nnvl.adapterApps.state.isEmpty()) {
            Text(
                text = Res.string.device_detail__layer_top__no_apps.rememberString(),
                style = MaterialTheme.typography.displaySmall,
                modifier = Modifier.align(Alignment.Center)
            )
        }

        AnimatedVisibility(
            visible = nnvl.adapterApps.change,
            enter = fadeIn(),
            exit = fadeOut(),
            modifier = Modifier.align(Alignment.BottomEnd)
                .padding(16.dp)
        ) {
            FloatingActionButton(
                onClick = {
                    action(DevicePreviewVM.Action.ReorderApps)
                },
                modifier = Modifier.align(Alignment.BottomEnd)
            ) { Icon(Svg.Content_save, null) }
        }

        AnimatedVisibility(
            visible = nnvl.adapterApps.uploadingChanges,
            modifier = Modifier.fillMaxSize()
        ) {
            LoadingOverlayUI()
        }
    }
}

@Composable
fun DevicePreviewScope.AppEntry(
    index: Int,
    nnvl: DevicePreviewScope.Wrapper,
    item: DevicePreviewScope.WrapperApps.Item,
    reorderState: ReorderableLazyListState
) {
    Row(Modifier.fillMaxWidth()) {
        Box(
            Modifier.size(80.dp)
                .detectReorder(reorderState)
        ) {
            Text(
                text = "${index + 1}",
                style = MaterialTheme.typography.displaySmall,
                textAlign = TextAlign.Center,
                modifier = Modifier.align(Alignment.Center)
            )
        }

        Row(
            Modifier
                .fillMaxWidth()
                .clickable {
                    val arg = AppDetailArgs(nnvl.data.company.id, item.data.id)
                    with(nav.findRoot()) {
                        when (AppType[item.data.type]) {
                            AppType.WHEEL -> navTo_AppPreviewWheelUI(arg)
                            AppType.KINDERGARTEN -> navTo_AppKindergartenPreviewUI(arg)
                            AppType.APP_GROUP,
                            AppType.BALOON,
                            AppType.CAMERA,
                            AppType.GALLERY,
                            AppType.IMAGE,
                            AppType.MAZE,
                            AppType.NONE,
                            AppType.PDF,
                            AppType.QUIZ,
                            AppType.RSS,
                            AppType.USB_PLAYER,
                            AppType.VIDEO,
                            AppType.VIDEO_ALPHA_LOOP,
                            AppType.WWW_EMBEDDED,
                            AppType.WWW_LINK -> navTo_AppDetailUI(arg)
                        }
                    }
                }
        ) {
            LazyImage(item.data.logo, Modifier.padding(8.dp).size(64.dp), alphaBackground = false)
            Column(
                modifier = Modifier.weight(1F)
                    .padding(vertical = 8.dp)
                    .padding(end = 8.dp)
            ) {
                Text(
                    text = item.data.name,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                    style = MaterialTheme.typography.bodyLarge,
                    modifier = Modifier.fillMaxWidth()
                )
                Text(
                    text = AppType[item.data.type].label,
                    overflow = TextOverflow.Ellipsis,
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.fillMaxWidth()
                )

                if (item.data.scheduled) {
                    Text(
                        text = Res.string.device_detail__layer_bottom__date_from_to.rememberString(item.data.start, item.data.end),
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
        }
    }
}